import React, { useEffect, useState, useCallback } from 'react';
import { supplierPublicService } from '../../services/supplierPublicService';
import Button from '../ui/Button';
import './SupplierResponsePage.css';

/**
 * #64 Feature 3 (Sessão 3) — página PÚBLICA do fornecedor (sem login).
 *
 * Acessada via /responder/{token} (link do email). Mostra a NC em Nível B
 * (problema + causa raiz textual) e permite UMA resposta em texto.
 *
 * Não usa o app autenticado — App.jsx roteia por pathname antes do login.
 * Chamadas via supplierPublicService (fetch puro, sem auth).
 */

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

function SupplierResponsePage({ token }) {
  const [nc, setNc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null); // {tipo, msg}
  // Camada 1 (#64 qualidade) — resposta estruturada (campos guiados) + observações.
  // #275 F1 — form completo (8D-like): confirmação + contenção rica.
  const [form, setForm] = useState({
    confirmacao: '',
    confirmacao_comentario: '',
    causa_identificada: '',
    acao_contencao: '',
    contencao_prazo: '',
    contencao_responsavel: '',
    prazo: '',
    resposta_texto: '',
  });
  // #275 F1 — listas dinâmicas: porquês + ações {o_que, quem, quando}.
  const [porques, setPorques] = useState(['']);
  const [acoesCorretivas, setAcoesCorretivas] = useState([{ o_que: '', quem: '', quando: '' }]);
  const [acoesPreventivas, setAcoesPreventivas] = useState([{ o_que: '', quem: '', quando: '' }]);
  const [enviando, setEnviando] = useState(false);
  const [enviada, setEnviada] = useState(false);
  // #74 — o link é reutilizável: o fornecedor reabre pra rever e pode EDITAR.
  const [editando, setEditando] = useState(false);
  // #74 — "reenviar meu link" (quando o link expirou/sumiu).
  const [recoverEmail, setRecoverEmail] = useState('');
  const [recoverEnviando, setRecoverEnviando] = useState(false);
  const [recoverMsg, setRecoverMsg] = useState('');
  // Camada 2 (#64) — evidência (upload). Independente da resposta: endpoint
  // próprio, disponível enquanto o link é válido (não é single-shot).
  const [evidFiles, setEvidFiles] = useState(null);
  const [enviandoEvid, setEnviandoEvid] = useState(false);
  const [evidEnviadas, setEvidEnviadas] = useState([]);
  const [erroEvid, setErroEvid] = useState(null);

  const setCampo = (chave) => (e) => setForm((f) => ({ ...f, [chave]: e.target.value }));
  // #275 F1 — o form adapta ao modo da NC (registro × investigação):
  // preventivas só na Investigação; porquês em ambos (opcional no Registro).
  const isInvestigacao = nc?.modo_analise === 'investigacao';
  const temPorque = porques.some((p) => p.trim());
  const temAcao = acoesCorretivas.some((a) => a.o_que.trim())
    || acoesPreventivas.some((a) => a.o_que.trim());
  const algumPreenchido = Object.values(form).some((v) => v.trim()) || temPorque || temAcao;

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const data = await supplierPublicService.verNC(token);
      setNc(data);
      // #74 — pré-preenche o form com a resposta anterior (pra rever/editar).
      const est = data.resposta_estruturada || {};
      setForm({
        confirmacao: est.confirmacao || '',
        confirmacao_comentario: est.confirmacao_comentario || '',
        causa_identificada: est.causa_identificada || '',
        acao_contencao: est.acao_contencao || '',
        contencao_prazo: est.contencao_prazo || '',
        contencao_responsavel: est.contencao_responsavel || '',
        prazo: est.prazo || '',
        resposta_texto: data.resposta_texto || '',
      });
      if (Array.isArray(est.porques) && est.porques.length) setPorques(est.porques);
      const toAcoes = (arr) => (Array.isArray(arr) && arr.length)
        ? arr.map((a) => ({ o_que: a.o_que || '', quem: a.quem || '', quando: a.quando || '' }))
        : [{ o_que: '', quem: '', quando: '' }];
      setAcoesCorretivas(toAcoes(est.acoes_corretivas));
      setAcoesPreventivas(toAcoes(est.acoes_preventivas));
      if (data.ja_respondida) {
        setEnviada(true);
      }
    } catch (err) {
      if (err.status === 404) {
        setErro({ tipo: 'invalido', msg: 'Este link é inválido ou expirou. Solicite um novo à empresa.' });
      } else if (err.status === 429) {
        setErro({ tipo: 'rate', msg: 'Muitas tentativas. Aguarde um momento e recarregue.' });
      } else {
        setErro({ tipo: 'erro', msg: 'Não foi possível carregar. Tente novamente em instantes.' });
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // #275 F1 — helpers das listas dinâmicas (caps espelham o backend).
  const setPorque = (i) => (e) =>
    setPorques((arr) => arr.map((p, idx) => (idx === i ? e.target.value : p)));
  const addPorque = () => setPorques((arr) => (arr.length < 10 ? [...arr, ''] : arr));
  const rmPorque = (i) => setPorques((arr) => arr.filter((_, idx) => idx !== i));

  const setAcao = (setter) => (i, chave) => (e) =>
    setter((arr) => arr.map((a, idx) => (idx === i ? { ...a, [chave]: e.target.value } : a)));
  const addAcao = (setter) => () =>
    setter((arr) => (arr.length < 10 ? [...arr, { o_que: '', quem: '', quando: '' }] : arr));
  const rmAcao = (setter) => (i) => setter((arr) => arr.filter((_, idx) => idx !== i));
  const setCorretiva = setAcao(setAcoesCorretivas);
  const setPreventiva = setAcao(setAcoesPreventivas);

  const handleEnviar = async (e) => {
    e.preventDefault();
    if (!algumPreenchido || enviando) return;
    setEnviando(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([chave, valor]) => {
        const limpo = valor.trim();
        if (limpo) payload[chave] = limpo;
      });
      // #275 F1 — listas: só itens preenchidos (backend re-valida/caps).
      const pq = porques.map((p) => p.trim()).filter(Boolean);
      if (pq.length) payload.porques = pq;
      const mapAcoes = (arr) => arr
        .filter((a) => a.o_que.trim())
        .map((a) => {
          const item = { o_que: a.o_que.trim() };
          if (a.quem.trim()) item.quem = a.quem.trim();
          if (a.quando.trim()) item.quando = a.quando.trim();
          return item;
        });
      const ac = mapAcoes(acoesCorretivas);
      if (ac.length) payload.acoes_corretivas = ac;
      const ap = mapAcoes(acoesPreventivas);
      if (ap.length) payload.acoes_preventivas = ap;

      const atualizada = await supplierPublicService.responder(token, payload);
      setNc(atualizada);
      setEnviada(true);
      setEditando(false); // #74 — volta ao modo revisão após salvar
    } catch (err) {
      if (err.status === 404) {
        setErro({ tipo: 'invalido', msg: 'Este link é inválido ou expirou.' });
      } else if (err.status === 429) {
        setErro({ tipo: 'rate', msg: 'Muitas tentativas. Aguarde um momento.' });
      } else {
        setErro({ tipo: 'erro', msg: `Não foi possível enviar: ${err.message}` });
      }
    } finally {
      setEnviando(false);
    }
  };

  // #74 — "reenviar meu link": o fornecedor perdeu o link/email. Resposta é
  // sempre genérica (o backend nunca revela se o email tem convite).
  const handleRecover = async (e) => {
    e.preventDefault();
    const email = recoverEmail.trim();
    if (recoverEnviando) return;
    if (!email || !email.includes('@')) {
      setRecoverMsg('Informe um e-mail válido.');
      return;
    }
    setRecoverEnviando(true);
    setRecoverMsg('');
    const generico = 'Se houver solicitações ativas para este e-mail, enviamos o link de acesso. Verifique sua caixa de entrada (e o spam).';
    try {
      const res = await supplierPublicService.recover(email);
      setRecoverMsg(res.mensagem || generico);
    } catch (err) {
      if (err.status === 429) setRecoverMsg('Muitas tentativas. Aguarde um momento e tente de novo.');
      else setRecoverMsg(generico); // não vaza nada mesmo em falha
    } finally {
      setRecoverEnviando(false);
    }
  };

  const handleEnviarEvidencia = async () => {
    if (!evidFiles || evidFiles.length === 0 || enviandoEvid) return;
    setEnviandoEvid(true);
    setErroEvid(null);
    try {
      const res = await supplierPublicService.enviarEvidencia(token, evidFiles);
      setEvidEnviadas((prev) => [...prev, ...(res.arquivos || [])]);
      setEvidFiles(null);
      const input = document.getElementById('sr-evid-input');
      if (input) input.value = '';
    } catch (err) {
      if (err.status === 409) setErroEvid('Limite de 3 evidências por solicitação atingido.');
      else if (err.status === 404) setErroEvid('Este link é inválido ou expirou.');
      else if (err.status === 429) setErroEvid('Muitas tentativas. Aguarde um momento.');
      else setErroEvid(err.message || 'Não foi possível enviar o(s) arquivo(s).');
    } finally {
      setEnviandoEvid(false);
    }
  };

  return (
    <div className="sr-page">
      <header className="sr-header">
        <span className="sr-brand">Qaa.sa</span>
      </header>

      <main className="sr-content">
        {loading && <div className="sr-aviso">Carregando…</div>}

        {!loading && erro && erro.tipo === 'invalido' && (
          <div className="sr-card sr-card-erro">
            <h1>Link indisponível</h1>
            <p>{erro.msg}</p>
            {/* #74 — "reenviar meu link": recupera o acesso por email */}
            <form className="sr-recover" onSubmit={handleRecover}>
              <label htmlFor="sr-recover-email">
                Perdeu o link? Informe o e-mail que recebeu o convite e reenviamos o acesso.
              </label>
              <div className="sr-recover-row">
                <input
                  id="sr-recover-email"
                  type="email"
                  value={recoverEmail}
                  onChange={(e) => setRecoverEmail(e.target.value)}
                  placeholder="seu@email.com"
                  disabled={recoverEnviando}
                />
                <Button
                  type="submit"
                  variant="primary"
                  icon="mail"
                  disabled={recoverEnviando || !recoverEmail.trim()}
                >
                  {recoverEnviando ? 'Enviando…' : 'Reenviar meu link'}
                </Button>
              </div>
              {recoverMsg && (
                <p className="sr-field-help" style={{ marginTop: '8px' }}>{recoverMsg}</p>
              )}
            </form>
          </div>
        )}

        {!loading && nc && (
          <article className="sr-card">
            <h1 className="sr-titulo">Solicitação de resposta — Não Conformidade</h1>
            <p className="sr-sub">
              Olá{nc.supplier_nome ? `, ${nc.supplier_nome}` : ''}. A empresa solicitou
              sua resposta sobre a não conformidade abaixo.
            </p>

            <section className="sr-nc">
              {nc.numero_nc && (
                <div className="sr-campo"><label>Número</label><p>{nc.numero_nc}</p></div>
              )}
              {nc.descricao_nc && (
                <div className="sr-campo"><label>Descrição do problema</label><p>{nc.descricao_nc}</p></div>
              )}
              <div className="sr-nc-grid">
                {nc.produto && (
                  <div className="sr-campo"><label>Produto</label><p>{nc.produto}</p></div>
                )}
                {nc.lote && (
                  <div className="sr-campo"><label>Lote</label><p>{nc.lote}</p></div>
                )}
                {formatDate(nc.data_ocorrencia) && (
                  <div className="sr-campo"><label>Data</label><p>{formatDate(nc.data_ocorrencia)}</p></div>
                )}
              </div>
              {/* #275 F1 anti-ancoragem: a causa da NC é HIPÓTESE do cliente,
                  não veredito — rotular assim evita que o fornecedor só
                  "concorde" em vez de fazer a análise independente dele. */}
              {nc.causa_raiz && (
                <div className="sr-campo">
                  <label>Hipótese inicial da empresa (a confirmar)</label>
                  <p>{nc.causa_raiz}</p>
                  <p className="sr-field-help" style={{ marginTop: '4px' }}>
                    Faça sua análise de forma independente — sua visão pode
                    confirmar ou contestar esta hipótese.
                  </p>
                </div>
              )}
            </section>

            {(enviada && !editando) ? (
              <div className="sr-enviada">
                <div className="sr-check"><span className="material-symbols-outlined" style={{ fontSize: '30px', lineHeight: '56px' }}>check</span></div>
                <h2>Resposta recebida</h2>
                <p>Obrigado. Sua resposta foi registrada e a empresa foi notificada.</p>
                {(() => {
                  // #275 F1 — eco COMPLETO da resposta (fonte: resposta_estruturada).
                  const est = nc.resposta_estruturada || {};
                  const CONF = {
                    concordo: 'Concordamos com o problema descrito',
                    concordo_parcial: 'Concordamos parcialmente',
                    contesto: 'Contestamos o problema descrito',
                  };
                  const temAlgo = Object.keys(est).length > 0 || nc.resposta_texto;
                  if (!temAlgo) return null;
                  return (
                    <div className="sr-resposta-eco">
                      {est.confirmacao && (
                        <div className="sr-campo"><label>Posição sobre o problema</label>
                          <p>{CONF[est.confirmacao] || est.confirmacao}{est.confirmacao_comentario ? ` — ${est.confirmacao_comentario}` : ''}</p></div>
                      )}
                      {est.acao_contencao && (
                        <div className="sr-campo"><label>Ação de contenção</label>
                          <p>{est.acao_contencao}
                            {est.contencao_prazo ? ` · Prazo: ${est.contencao_prazo}` : ''}
                            {est.contencao_responsavel ? ` · Responsável: ${est.contencao_responsavel}` : ''}</p></div>
                      )}
                      {est.causa_identificada && (
                        <div className="sr-campo"><label>Causa identificada</label><p>{est.causa_identificada}</p></div>
                      )}
                      {Array.isArray(est.porques) && est.porques.length > 0 && (
                        <div className="sr-campo"><label>Cadeia de porquês</label>
                          {est.porques.map((p, i) => (<p key={i}>{i + 1}. {p}</p>))}</div>
                      )}
                      {Array.isArray(est.acoes_corretivas) && est.acoes_corretivas.length > 0 && (
                        <div className="sr-campo"><label>Ações corretivas</label>
                          {est.acoes_corretivas.map((a, i) => (
                            <p key={i}>• {a.o_que}{a.quem ? ` — ${a.quem}` : ''}{a.quando ? ` (${a.quando})` : ''}</p>
                          ))}</div>
                      )}
                      {Array.isArray(est.acoes_preventivas) && est.acoes_preventivas.length > 0 && (
                        <div className="sr-campo"><label>Ações preventivas</label>
                          {est.acoes_preventivas.map((a, i) => (
                            <p key={i}>• {a.o_que}{a.quem ? ` — ${a.quem}` : ''}{a.quando ? ` (${a.quando})` : ''}</p>
                          ))}</div>
                      )}
                      {est.acao_corretiva && (
                        <div className="sr-campo"><label>Ação corretiva</label><p>{est.acao_corretiva}</p></div>
                      )}
                      {est.prazo && (
                        <div className="sr-campo"><label>Prazo</label><p>{est.prazo}</p></div>
                      )}
                      {nc.resposta_texto && (
                        <div className="sr-campo"><label>Observações</label><p>{nc.resposta_texto}</p></div>
                      )}
                    </div>
                  );
                })()}
                {/* #74 — link reutilizável: o fornecedor pode editar/complementar */}
                <div className="sr-enviada-editar">
                  <p className="sr-field-help">
                    Precisa corrigir ou complementar? Você pode editar sua
                    resposta enquanto este link estiver ativo.
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    icon="edit"
                    onClick={() => { setEditando(true); setErro(null); }}
                  >
                    Editar minha resposta
                  </Button>
                </div>
              </div>
            ) : (
              <form className="sr-form" onSubmit={handleEnviar}>
                {/* #74 — modo edição de uma resposta já enviada */}
                {editando && (
                  <div className="sr-instrucao">
                    <label>Editando sua resposta</label>
                    <p>Ajuste o que precisar e salve. A versão anterior fica registrada para a empresa.</p>
                  </div>
                )}
                {/* #64 S4 — instrução do tenant (o que ele espera da resposta) */}
                {nc.mensagem_instrucao && (
                  <div className="sr-instrucao">
                    <label>O que a empresa pede</label>
                    <p>{nc.mensagem_instrucao}</p>
                  </div>
                )}
                <p className="sr-form-help">
                  Responda os campos abaixo com o que se aplica ao seu caso. Você
                  não precisa preencher todos — pelo menos um já ajuda a empresa a
                  tratar a não conformidade.
                </p>

                {/* #275 F1 — 1. Confirmação do problema */}
                <div className="sr-field">
                  <label>Sua posição sobre o problema descrito</label>
                  <div className="sr-radio-row">
                    {[
                      ['concordo', 'Concordo'],
                      ['concordo_parcial', 'Concordo parcialmente'],
                      ['contesto', 'Contesto'],
                    ].map(([valor, rotulo]) => (
                      <label key={valor} className="sr-radio">
                        <input
                          type="radio"
                          name="sr-confirmacao"
                          value={valor}
                          checked={form.confirmacao === valor}
                          onChange={setCampo('confirmacao')}
                          disabled={enviando}
                        />
                        {rotulo}
                      </label>
                    ))}
                  </div>
                  {form.confirmacao && (
                    <textarea
                      value={form.confirmacao_comentario}
                      onChange={setCampo('confirmacao_comentario')}
                      rows={2}
                      maxLength={2000}
                      placeholder={form.confirmacao === 'contesto'
                        ? 'Explique por que contesta (ex.: o lote citado não saiu da nossa planta).'
                        : 'Comentário (opcional).'}
                      disabled={enviando}
                      style={{ marginTop: '8px' }}
                    />
                  )}
                </div>

                {/* 2. Contenção imediata (descrição + prazo + responsável) */}
                <div className="sr-field">
                  <label htmlFor="sr-contencao">Ação de contenção (imediata)</label>
                  <p className="sr-field-help">O que você já fez para conter o problema agora?</p>
                  <textarea
                    id="sr-contencao"
                    value={form.acao_contencao}
                    onChange={setCampo('acao_contencao')}
                    rows={3}
                    maxLength={2000}
                    placeholder="Ex: Segregamos o lote afetado e inspecionamos o estoque remanescente."
                    disabled={enviando}
                  />
                  <div className="sr-inline-grid">
                    <input
                      type="text"
                      value={form.contencao_prazo}
                      onChange={setCampo('contencao_prazo')}
                      maxLength={200}
                      placeholder="Prazo da contenção (ex: 48h)"
                      disabled={enviando}
                    />
                    <input
                      type="text"
                      value={form.contencao_responsavel}
                      onChange={setCampo('contencao_responsavel')}
                      maxLength={200}
                      placeholder="Responsável (ex: João — PCP)"
                      disabled={enviando}
                    />
                  </div>
                </div>

                {/* 3. Causa raiz identificada pelo fornecedor */}
                <div className="sr-field">
                  <label htmlFor="sr-causa">Causa identificada (do seu lado)</label>
                  <p className="sr-field-help">O que, no seu processo, levou ao problema?</p>
                  <textarea
                    id="sr-causa"
                    value={form.causa_identificada}
                    onChange={setCampo('causa_identificada')}
                    rows={3}
                    maxLength={2000}
                    placeholder="Ex: Desvio no processo de têmpera — temperatura abaixo do especificado."
                    disabled={enviando}
                  />
                </div>

                {/* 4. Cadeia de porquês (#275 F1 — opcional; guia o método) */}
                <div className="sr-field">
                  <label>Cadeia de porquês (opcional)</label>
                  <p className="sr-field-help">
                    Pergunte "por quê?" em sequência até chegar à origem — cada
                    resposta alimenta a próxima pergunta.
                  </p>
                  {porques.map((p, i) => (
                    <div key={i} className="sr-lista-item">
                      <span className="sr-lista-num">{i + 1}.</span>
                      <input
                        type="text"
                        value={p}
                        onChange={setPorque(i)}
                        maxLength={1000}
                        placeholder={i === 0 ? 'Por que o problema ocorreu?' : `Por quê? (aprofunda o porquê ${i})`}
                        disabled={enviando}
                      />
                      {porques.length > 1 && (
                        <button type="button" className="sr-lista-rm" onClick={() => rmPorque(i)} disabled={enviando} title="Remover">×</button>
                      )}
                    </div>
                  ))}
                  {porques.length < 10 && (
                    <button type="button" className="sr-lista-add" onClick={addPorque} disabled={enviando}>
                      + Adicionar porquê
                    </button>
                  )}
                </div>

                {/* 5. Ações corretivas (lista o quê/quem/quando) */}
                <div className="sr-field">
                  <label>Ações corretivas (evitar recorrência)</label>
                  <p className="sr-field-help">O que vai fazer para o problema não voltar? Uma linha por ação.</p>
                  {acoesCorretivas.map((a, i) => (
                    <div key={i} className="sr-acao-item">
                      <textarea
                        value={a.o_que}
                        onChange={setCorretiva(i, 'o_que')}
                        rows={2}
                        maxLength={2000}
                        placeholder={`Ação ${i + 1} — o que será feito`}
                        disabled={enviando}
                      />
                      <div className="sr-inline-grid">
                        <input type="text" value={a.quem} onChange={setCorretiva(i, 'quem')} maxLength={200} placeholder="Quem (responsável)" disabled={enviando} />
                        <input type="text" value={a.quando} onChange={setCorretiva(i, 'quando')} maxLength={200} placeholder="Quando (prazo)" disabled={enviando} />
                      </div>
                      {acoesCorretivas.length > 1 && (
                        <button type="button" className="sr-lista-rm sr-acao-rm" onClick={() => rmAcao(setAcoesCorretivas)(i)} disabled={enviando} title="Remover ação">×</button>
                      )}
                    </div>
                  ))}
                  {acoesCorretivas.length < 10 && (
                    <button type="button" className="sr-lista-add" onClick={addAcao(setAcoesCorretivas)} disabled={enviando}>
                      + Adicionar ação corretiva
                    </button>
                  )}
                </div>

                {/* 6. Ações preventivas — só Investigação (#96: form adapta ao modo) */}
                {isInvestigacao && (
                  <div className="sr-field">
                    <label>Ações preventivas (opcional)</label>
                    <p className="sr-field-help">Ações para evitar problemas semelhantes em outros processos/produtos.</p>
                    {acoesPreventivas.map((a, i) => (
                      <div key={i} className="sr-acao-item">
                        <textarea
                          value={a.o_que}
                          onChange={setPreventiva(i, 'o_que')}
                          rows={2}
                          maxLength={2000}
                          placeholder={`Ação preventiva ${i + 1}`}
                          disabled={enviando}
                        />
                        <div className="sr-inline-grid">
                          <input type="text" value={a.quem} onChange={setPreventiva(i, 'quem')} maxLength={200} placeholder="Quem" disabled={enviando} />
                          <input type="text" value={a.quando} onChange={setPreventiva(i, 'quando')} maxLength={200} placeholder="Quando" disabled={enviando} />
                        </div>
                        {acoesPreventivas.length > 1 && (
                          <button type="button" className="sr-lista-rm sr-acao-rm" onClick={() => rmAcao(setAcoesPreventivas)(i)} disabled={enviando} title="Remover ação">×</button>
                        )}
                      </div>
                    ))}
                    {acoesPreventivas.length < 10 && (
                      <button type="button" className="sr-lista-add" onClick={addAcao(setAcoesPreventivas)} disabled={enviando}>
                        + Adicionar ação preventiva
                      </button>
                    )}
                  </div>
                )}

                {/* 7. Prazo global + 8. Observações */}
                <div className="sr-field">
                  <label htmlFor="sr-prazo">Prazo geral</label>
                  <p className="sr-field-help">Quando as ações estarão concluídas?</p>
                  <input
                    id="sr-prazo"
                    type="text"
                    value={form.prazo}
                    onChange={setCampo('prazo')}
                    maxLength={200}
                    placeholder="Ex: 30 dias / até 15/07/2026"
                    disabled={enviando}
                  />
                </div>

                <div className="sr-field">
                  <label htmlFor="sr-obs">Observações (opcional)</label>
                  <textarea
                    id="sr-obs"
                    value={form.resposta_texto}
                    onChange={setCampo('resposta_texto')}
                    rows={4}
                    maxLength={10000}
                    placeholder="Qualquer informação adicional que ajude a empresa."
                    disabled={enviando}
                  />
                </div>

                <div className="sr-form-footer">
                  {editando ? (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => { setEditando(false); setErro(null); }}
                      disabled={enviando}
                    >
                      Cancelar
                    </Button>
                  ) : <span className="sr-contador" />}
                  <Button type="submit" variant="primary" icon="send" disabled={enviando || !algumPreenchido}>
                    {enviando ? 'Enviando…' : (editando ? 'Salvar alterações' : 'Enviar resposta')}
                  </Button>
                </div>
                {erro && erro.tipo !== 'invalido' && (
                  <div className="sr-erro-inline">{erro.msg}</div>
                )}
              </form>
            )}

            {/* Camada 2 (#64) — anexar evidência. Independente da resposta:
                endpoint próprio, disponível enquanto o link é válido. */}
            <section className="sr-evidencia">
              <h2 className="sr-evid-titulo">Anexar evidência (opcional)</h2>
              <p className="sr-evid-help">
                Certificado, foto, laudo. PDF, PNG ou JPG · até 5 MB cada · no
                máximo 3 arquivos.
              </p>
              {evidEnviadas.length > 0 && (
                <ul className="sr-evid-lista">
                  {evidEnviadas.map((nome, i) => (
                    <li key={i}>
                      <span className="material-symbols-outlined">check</span>
                      {nome}
                    </li>
                  ))}
                </ul>
              )}
              <div className="sr-evid-acoes">
                <input
                  id="sr-evid-input"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,image/png,image/jpeg,application/pdf"
                  multiple
                  onChange={(e) => setEvidFiles(e.target.files)}
                  disabled={enviandoEvid}
                />
                <Button
                  type="button"
                  variant="secondary"
                  icon="upload_file"
                  onClick={handleEnviarEvidencia}
                  disabled={enviandoEvid || !evidFiles || evidFiles.length === 0}
                >
                  {enviandoEvid ? 'Enviando…' : 'Enviar arquivo(s)'}
                </Button>
              </div>
              {erroEvid && <div className="sr-erro-inline">{erroEvid}</div>}
            </section>
          </article>
        )}
      </main>

      <footer className="sr-footer">
        <p>Qaa.sa — Quality as a Service</p>
      </footer>
    </div>
  );
}

export default SupplierResponsePage;
